import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { useRef } from 'react';
import { fieldHasContent, transformProperties } from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { useIntersectionObserver } from 'remote-com-shared/utils/hooks';
import styled from 'styled-components';

import {
  SectionLabel,
  SectionTitleComponent,
  OverviewDescriptionComponent,
} from '@/src/components/OverviewSection/OverviewSectionRow';
import { components } from '@/src/views/InternationalGlobalPayroll/slices//GlobalPayrollSlice';
import { BestPracticesContainer } from '@/src/views/InternationalGlobalPayroll/slices/BestPracticesSlice';
import {
  Description,
  BlockImage,
  BlockContent,
  BestPracticeBlock,
  StepTitle,
  SectionHeader,
  BlockNumber,
} from '@/src/views/Services/GlobalPayroll/slices/BestPracticesSlice';

const DescriptionWrapper = styled(Description)`
  animation: descriptionAppearAnimation 1.5s ease ${({ index }) => (index + 1) * 1.3}s;
  animation-fill-mode: both;
  overflow: hidden;

  @keyframes descriptionAppearAnimation {
    0% {
      max-height: 0;
      opacity: 0;
    }

    100% {
      max-height: 160px;
      opacity: 1;
    }
  }
`;

const Container = styled(BestPracticesContainer)`
  ${fluidProp('padding-bottom', '50px', '100px')};

  ${DescriptionWrapper} {
    ${({ $isVisible }) =>
      $isVisible ? `animation-play-state: running;` : `animation-play-state: paused;`};
  }
`;

const Section = styled.div`
  background-image: var(--colors-zircon);
`;

const StepContent = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

function StepBlock({ step, index }) {
  const { stepTitle, stepDescription, illustration } = step;

  return (
    <BestPracticeBlock key={index}>
      <BlockNumber>{index + 1}</BlockNumber>
      <BlockContent>
        <BlockImage>
          {illustration && (
            <Image
              src={illustration?.url}
              alt={illustration?.alt || ''}
              width={illustration?.dimensions.width}
              height={illustration?.dimensions.height}
              quality="100"
            />
          )}
        </BlockImage>
        <StepContent>
          {fieldHasContent(stepTitle) && (
            <StepTitle index={index}>
              <PrismicRichText field={stepTitle} />
            </StepTitle>
          )}
          {fieldHasContent(stepDescription) && (
            <DescriptionWrapper index={index}>
              <PrismicRichText field={stepDescription} components={components} />
            </DescriptionWrapper>
          )}
        </StepContent>
      </BlockContent>
    </BestPracticeBlock>
  );
}

export function ProcessSlice({ slice }) {
  const { primary, fields } = transformProperties(slice);
  const { label, title, description } = primary;

  const hasHeading =
    fieldHasContent(label) || fieldHasContent(title) || fieldHasContent(description);
  const sectionRef = useRef();
  const isVisible = useIntersectionObserver(sectionRef, {
    threshold: 0,
    rootMargin: '0px',
    unobserve: true,
  });
  return (
    <Section>
      <Container ref={sectionRef} $isVisible={isVisible}>
        {hasHeading && (
          <SectionHeader>
            {fieldHasContent(label) && <SectionLabel>{label}</SectionLabel>}
            {fieldHasContent(title) && (
              <SectionTitleComponent>
                <PrismicRichText field={title} />
              </SectionTitleComponent>
            )}
            {fieldHasContent(description) && (
              <OverviewDescriptionComponent>
                <PrismicRichText field={description} />
              </OverviewDescriptionComponent>
            )}
          </SectionHeader>
        )}
        <div>
          {fields?.map((step, index) => (
            <StepBlock key={index} step={step} index={index} />
          ))}
        </div>
      </Container>
    </Section>
  );
}
